// import "@/utils/sso";
import Cookies from "js-cookie";
import { getConfig } from "@/config";
import NProgress from "@/utils/progress";
import { transformI18n } from "@/plugins/i18n";
import { buildHierarchyTree } from "@/utils/tree";
import { redirectUtils } from "@/utils/redirect.ts ";
import remainingRouter from "./modules/remaining";
import { useMultiTagsStoreHook } from "@/store/modules/multiTags";
import { usePermissionStoreHook } from "@/store/modules/permission";
import { isUrl, openLink, storageLocal, isAllEmpty } from "@pureadmin/utils";
import {
  ascending,
  getTopMenu,
  initRouter,
  isOneOfArray,
  getHistoryMode,
  findRouteByPath,
  handleAliveRoute,
  formatTwoStageRoutes,
  formatFlatteningRoutes
} from "./utils";
import {
  type Router,
  createRouter,
  type RouteRecordRaw,
  type RouteComponent
} from "vue-router";
import {
  type DataInfo,
  userKey,
  TokenKey,
  removeToken,
  multipleTabsKey
} from "@/utils/auth";
import envConfig from "@/config/env";
import { checkWeComEnvironment } from "../views/login/utils/wecom";
import store from "store2";
import { useUserStoreHook } from "@/store/modules/user";

/** 自动导入全部静态路由，无需再手动引入！匹配 src/router/modules 目录（任何嵌套级别）中具有 .ts 扩展名的所有文件，除了 remaining.ts 文件
 * 如何匹配所有文件请看：https://github.com/mrmlnc/fast-glob#basic-syntax
 * 如何排除文件请看：https://cn.vitejs.dev/guide/features.html#negative-patterns
 */
const modules: Record<string, any> = import.meta.glob(
  ["./modules/**/*.ts", "!./modules/**/remaining.ts"],
  {
    eager: true
  }
);

/** 原始静态路由（未做任何处理） */
const routes = [];

Object.keys(modules).forEach(key => {
  routes.push(modules[key].default);
});

/**本地 路由 判断是否使用本地路由*/
if (envConfig.isLocalRoute) {
  const localModule: Record<string, any> = import.meta.glob(
    ["./local/**/*.ts"],
    {
      eager: true
    }
  );
  Object.keys(localModule).forEach(key => {
    routes.push(localModule[key].default);
  });
}

/** 导出处理后的静态路由（三级及以上的路由全部拍成二级） */
export const constantRoutes: Array<RouteRecordRaw> = formatTwoStageRoutes(
  formatFlatteningRoutes(buildHierarchyTree(ascending(routes.flat(Infinity))))
);

/** 用于渲染菜单，保持原始层级 */
export const constantMenus: Array<RouteComponent> = ascending(
  routes.flat(Infinity)
).concat(...remainingRouter);

/** 不参与菜单的路由 */
export const remainingPaths = Object.keys(remainingRouter).map(v => {
  return remainingRouter[v].path;
});

/** 创建路由实例 */
export const router: Router = createRouter({
  history: getHistoryMode(import.meta.env.VITE_ROUTER_HISTORY),
  routes: constantRoutes.concat(...(remainingRouter as any)),
  strict: true,
  scrollBehavior(to, from, savedPosition) {
    return new Promise(resolve => {
      if (savedPosition) {
        return savedPosition;
      } else {
        if (from.meta.saveSrollTop) {
          const top: number =
            document.documentElement.scrollTop || document.body.scrollTop;
          resolve({ left: 0, top });
        }
      }
    });
  }
});

/** 重置路由 */
export function resetRouter() {
  router.getRoutes().forEach(route => {
    const { name, meta } = route;
    if (name && router.hasRoute(name) && meta?.backstage) {
      router.removeRoute(name);
      router.options.routes = formatTwoStageRoutes(
        formatFlatteningRoutes(
          buildHierarchyTree(ascending(routes.flat(Infinity)))
        )
      );
    }
  });
  usePermissionStoreHook().clearAllCachePage();
}

/** 路由白名单 */
const whiteList = ["/login"];

const { VITE_HIDE_HOME } = import.meta.env;

router.beforeEach(async (to, _from, next) => {
  NProgress.start();

  const whiteList = ["/login"];
  const goToLogin = () => {
    redirectUtils.saveRedirectPath(to.fullPath);
    next("/login");
  };

  // ✅ 1. 白名单
  if (whiteList.includes(to.path)) {
    next();
    return;
  }

  let token = Cookies.get(TokenKey);
  let userInfo = storageLocal().getItem(userKey) as DataInfo<number> | null;

  // ❗ 2. 没 token → 尝试 refresh
  if (!token) {
    if (userInfo?.refresh_token) {
      try {
        await useUserStoreHook().handRefreshToken({
          refresh_token: userInfo.refresh_token
        });

        // 🔥 refresh 后必须重新读取
        token = Cookies.get(TokenKey);
        userInfo = storageLocal().getItem(userKey);

        if (!token) {
          throw new Error("refresh failed");
        }

        next({ path: to.fullPath, replace: true });
        return;
      } catch (e) {
        removeToken();
        goToLogin();
        return;
      }
    }

    removeToken();
    goToLogin();
    return;
  }

  // ❗ 3. 有 token 但检查是否过期 → 自动刷新
  if (token && userInfo?.expires) {
    const now = new Date().getTime();
    const isExpired = parseInt(userInfo.expires.toString()) - now <= 0;

    if (isExpired && userInfo.refresh_token) {
      try {
        console.log("检测到token过期，自动刷新...");
        await useUserStoreHook().handRefreshToken({
          refresh_token: userInfo.refresh_token
        });

        // 🔥 refresh 后必须重新读取
        token = Cookies.get(TokenKey);
        userInfo = storageLocal().getItem(userKey);

        if (!token) {
          throw new Error("refresh failed");
        }

        console.log("token刷新成功，继续跳转");
        next({ path: to.fullPath, replace: true });
        return;
      } catch (e) {
        console.error("token刷新失败:", e);
        removeToken();
        goToLogin();
        return;
      }
    }
  }

  // ❗ 4. 没 userInfo（补救）
  if (!userInfo) {
    removeToken();
    goToLogin();
    return;
  }

  // ❗ 5. 动态路由初始化
  if (!usePermissionStoreHook().isRoutesLoaded) {
    console.log("开始初始化动态路由...");
    try {
      await initRouter();
      usePermissionStoreHook().isRoutesLoaded = true;
      console.log("动态路由初始化成功，准备跳转到:", to.fullPath);

      next({ path: to.fullPath, replace: true });
      return;
    } catch (e) {
      console.error("动态路由初始化失败:", e);
      console.log("清除 Token 并跳转到登录页");
      removeToken();
      goToLogin();
      return;
    }
  }
  if (to.meta?.keepAlive) {
    handleAliveRoute(to as ToRouteType, "add");

    if (_from.name === undefined || _from.name === "Redirect") {
      handleAliveRoute(to as ToRouteType);
    }
  }

  console.log("路由已加载，直接放行");
  next();
});

router.onError((error, to) => {
  if (error.message.includes("Failed to fetch dynamically imported module")) {
    const targetHref = router.resolve(to.fullPath).href;
    const targetUrl = new URL(targetHref, window.location.href).href;
    if (targetUrl === window.location.href) {
      window.location.reload();
    } else {
      window.location.assign(targetHref);
    }
  }
});

router.afterEach(() => {
  NProgress.done();
});

export default router;
